import { RequestCookies } from 'next/dist/compiled/@edge-runtime/cookies';
import { NextRequest } from 'next/server';
import { GraphQLData, GraphQLType, RequestData } from './middleware';

/**
 * This function is used to attach the list of headers from the DataDome's protection API response to the incoming request.
 * This function is mainly used to attach the DataDome's enriched headers.
 * @see {@link https://docs.datadome.co/docs/logs-integration} for the available enriched headers.
 * @param reqHeaders - The headers of the incoming request.
 * @param dataDomeResHeaders - The headers of the DataDome's protection API response.
 * @param listKey - The header key we want to retrieve.
 * @returns A map of string.
 */
export function convertHeadersToMap(
    reqHeaders: Headers,
    dataDomeResHeaders: Headers,
    listKey: string,
): Map<string, string> {
    const map = new Map<string, string>();
    const list = dataDomeResHeaders.get(listKey);
    if (!list) {
        return map;
    }
    for (const header of list.split(' ')) {
        const value = dataDomeResHeaders.get(header);
        if (value) {
            if (header.toLowerCase() === 'set-cookie') {
                const cookies = value.split(',').map((cookie) => cookie.trim());
                for (const cookie of cookies) {
                    if (!cookie) continue;

                    // workaround for a bug in DataDome where the cookie domain gets set to
                    // the entire public suffix (.vercel.app), which UAs refuse to set cookies for
                    // e.g.: https://devcenter.heroku.com/articles/cookies-and-herokuapp-com
                    const host = reqHeaders.get('host');
                    let processedCookie = cookie;

                    if (host && /domain=\.vercel\.app/i.test(cookie)) {
                        processedCookie = cookie.replace(/domain=\.vercel\.app/i, `Domain=${host}`);
                    }

                    const existingCookies = map.get('set-cookie') || '';
                    map.set('set-cookie', existingCookies ? `${existingCookies},${processedCookie}` : processedCookie);
                }
            } else {
                map.set(header, value);
            }
        }
    }
    return map;
}

/**
 * This functions is used to return all the headers from the request in a string separated by commas.
 * @param req - The incoming request.
 * @returns A comma separated list of headers.
 */
export function getHeadersList(req: NextRequest): string {
    return [...req.headers.keys()].join(',');
}

/**
 * This function is used to return the length of the Authorization header.
 * @param req - The incoming request.
 * @returns The length of the Authorization header.
 */
export function getAuthorizationLength(req: NextRequest): number | undefined {
    const authorization = req.headers.get('authorization');
    return authorization?.length;
}

/**
 * This function is used to create an URL-encoded string of the payload of the Protection API.
 * @param obj - The truncated payload for the Protection API.
 * @returns An URL-encoded string of the truncated payload.
 */
export function stringify(obj: RequestData) {
    return obj
        ? (Object.keys(obj) as (keyof RequestData)[])
              .reduce<string[]>((acc, key) => {
                  const value = obj[key];
                  if (value == null) {
                      return acc;
                  }
                  acc.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
                  return acc;
              }, [])
              .join('&')
        : '';
}

/**
 * Returns the value of the `datadome` cookie.
 * @param cookies - the cookies of the request.
 * @returns The Client ID of the request.
 */
export function getCookieData(cookies: RequestCookies): string {
    const datadomeCookie = cookies.get('datadome');
    if (datadomeCookie !== undefined) {
        return datadomeCookie.value;
    }
    return '';
}

/**
 * Determine if the incoming request is a GraphQL request
 * @param {Object} request
 * @param {URL} request.url - The URL of the request.
 * @param {string} request.method - The HTTP method of the request.
 * @param {boolean} request.bodyExists - Indicate if the request body is present.
 * @param {string} request.contentType - The Content-Type of the request.
 * @returns {boolean} - Returns `true` if the request is likely a GraphQL request, `false` otherwise.
 */
export function isGraphQLRequest({
    url,
    method,
    bodyExists,
    contentType,
}: {
    url: URL;
    method: string;
    bodyExists: boolean;
    contentType: string | null;
}): boolean {
    if (method === 'POST' && bodyExists == true && contentType?.includes('application/json')) {
        return url.pathname.toLowerCase().includes('graphql');
    }
    return false;
}

/**
 * Returns true if at least one GraphQL operation was detected.
 * @param {GraphQLData} graphQLData - GraphQL properties extracted from a request
 * @returns {boolean} true if the object contains GraphQL data
 */
export function isGraphQLDataAvailable(graphQLData: GraphQLData): boolean {
    return graphQLData != null && graphQLData['count'] > 0;
}

/**
 * Fetch the GraphQL query from the query parameters.
 * @param {URL} fullUrl
 * @returns {string|null}
 */
function getGraphQLQueryStringFromQueryParams(fullUrl: URL): string | null {
    return fullUrl.searchParams.get('query');
}

/**
 * Fetch the GraphQL query from the body.
 * @param {NextRequest} request
 * @param {number} maximumBodySize
 * @returns {Promise<string | null>}
 */
async function getGraphQLQueryStringFromBody(request: Request, maximumBodySize: number): Promise<string | null> {
    // Read the body chunk by chunk
    const regex = /"query"\s*:\s*(".*)/;
    const textDecoder = new TextDecoder();
    if (request.body === null) {
        return null;
    }
    const reader = request.body.getReader();
    const chunkSize = 1024;
    let iteration = 0;
    let bodyString = '';
    let match: RegExpMatchArray | null = null;

    const { value, done } = await reader.read();

    if (value === undefined && done === true) {
        return null;
    }

    const chunk = textDecoder.decode(value, { stream: !done });
    while (iteration * chunkSize < maximumBodySize) {
        // We only keep the 2 last chunks to do not perform the regex matching on a large string
        bodyString += chunk.slice(iteration * chunkSize, (iteration + 2) * chunkSize);

        match = bodyString.match(regex);
        if (match !== null && match.length > 0) {
            return match[1];
        }
        iteration += 1;
    }
    return null;
}

/**
 * Collect the GraphQL information from the request
 * @param {NextRequest} request
 * @param {URL} fullUrl
 * @param {number} maximumBodySize
 * @returns {GraphQLData}
 */
export async function collectGraphQL(
    request: NextRequest,
    fullUrl: URL,
    maximumBodySize: number,
): Promise<GraphQLData> {
    const result: GraphQLData = {
        name: '',
        type: 'query',
        count: 0,
    };

    let queryString: Promise<string | null> | string | null;
    queryString = getGraphQLQueryStringFromQueryParams(fullUrl);
    if (queryString == null) {
        const clonedRequest = request.clone();
        queryString = await getGraphQLQueryStringFromBody(clonedRequest, maximumBodySize);
    }
    if (queryString == null) {
        return result;
    }
    const regex = /(?<operationType>query|mutation|subscription)\s*(?<operationName>[A-Za-z_][A-Za-z0-9_]*)?\s*[({@]/gm;

    const matches = Array.from(queryString.matchAll(regex));
    let matchLength = matches.length;
    if (matchLength > 0) {
        result.type = (matches[0].groups?.operationType ?? 'query') as GraphQLType;
        result.name = matches[0].groups?.operationName ?? '';
    } else {
        const shorthandSyntaxRegex =
            /"(?<operationType>(?:query|mutation|subscription))?\s*(?<operationName>[A-Za-z_][A-Za-z0-9_]*)?\s*[({@]/gm;
        const shorthandSyntaxMatches = Array.from(queryString.matchAll(shorthandSyntaxRegex));
        matchLength = shorthandSyntaxMatches.length;
    }
    result['count'] = matchLength;
    return result;
}

/**
 * Parse a Set-Cookie header string and extract cookie attributes.
 *
 * This function parses all standard Set-Cookie attributes as defined in:
 * https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Set-Cookie
 *
 * @param cookieString - The Set-Cookie header value (e.g., "name=value; Path=/; Max-Age=3600")
 * @returns Parsed cookie data with name, value, and options, or null if invalid
 */
export function parseCookieAttributes(cookieString: string): {
    name: string;
    value: string;
    options: {
        secure?: boolean;
        httpOnly?: boolean;
        domain?: string;
        path?: string;
        sameSite?: 'lax' | 'strict' | 'none';
        maxAge?: number;
        expires?: Date;
        partitioned?: boolean;
    };
} | null {
    const [nameValue, ...attrs] = cookieString.split(';');
    const [name, val] = nameValue.split('=');
    if (!name || !val) {
        return null;
    }

    const options: {
        secure?: boolean;
        httpOnly?: boolean;
        domain?: string;
        path?: string;
        sameSite?: 'lax' | 'strict' | 'none';
        maxAge?: number;
        expires?: Date;
        partitioned?: boolean;
    } = {};

    attrs.forEach((attr) => {
        const [k, v] = attr.trim().split('=');
        const key = k.toLowerCase();

        switch (key) {
            case 'secure':
                options.secure = true;
                break;
            case 'httponly':
                options.httpOnly = true;
                break;
            case 'domain':
                if (v) options.domain = v;
                break;
            case 'path':
                if (v) options.path = v;
                break;
            case 'samesite':
                if (v) {
                    const sameSiteValue = v.toLowerCase() as 'lax' | 'strict' | 'none';
                    options.sameSite = sameSiteValue;
                }
                break;
            case 'max-age':
                if (v) options.maxAge = parseInt(v, 10);
                break;
            case 'expires':
                if (v) options.expires = new Date(v);
                break;
            case 'partitioned':
                options.partitioned = true;
                break;
        }
    });

    return {
        name: name.trim(),
        value: val.trim(),
        options,
    };
}
